import { Tooltip } from "@mui/material";
import type { ComponentType, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { IconName } from "../Icon/Icon";
import Icon from "../Icon/Icon";

export interface SideBarItemProps {
  icon?: IconName;
  routeTo: string;
  title: string | JSX.Element;
  onClick?: () => void;
  disabled?: boolean;
  tooltipText?: string;
  fillIcon?: string;
  isSelected?: boolean;
  dataTour?: string;
  customClassName?: string;
  textCustomClassName?: string;
  customIcon?: JSX.Element | null;
}

const SidebarItemProject = ({
  icon,
  routeTo,
  title,
  disabled,
  tooltipText,
  customClassName = "",
  textCustomClassName = "",
  customIcon = undefined,
  ...props
}: SideBarItemProps) => {
  let button = (
    <button
      className={`${
        disabled ? "disabled:opacity-50 cursor-not-allowed" : "hover:bg-blue-300 cursor-pointer "
      } flex flex-col w-full my-1 px-2 min-h-24 py-2 items-center bg-blue-500 rounded-md ${customClassName}`}
      {...props}
      disabled={disabled}
    >
      {customIcon ? (
        customIcon
      ) : (
        <Icon icon={icon as IconName} className="w-6 h-6 stroke-green-600 m-auto" />
      )}
      <span className={`text-white ${textCustomClassName}`}>{title}</span>
    </button>
  );
  if (tooltipText && tooltipText !== "") {
    button = <Tooltip title={tooltipText}>{button}</Tooltip>;
  }

  if (!disabled && routeTo !== "") {
    button = <Link to={routeTo}>{button}</Link>;
  }

  return button;
};

export interface SidebarNavRowProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  to?: string;
  onClick?: () => void;
  isSelected?: boolean;
  dataTour?: string;
  trailing?: ReactNode;
  accessory?: ReactNode;
  iconClassName?: string;
  labelClassName?: string;
  renderPrimary?: (className: string, content: ReactNode) => ReactNode;
  itemId?: string;
  favoritable?: boolean;
  noDrag?: boolean;
  grip?: ReactNode;
}

const SidebarNavRow = ({
  icon: RowIcon,
  title,
  to,
  onClick,
  isSelected = false,
  dataTour,
  trailing,
  accessory,
  iconClassName = "stroke-white",
  labelClassName = "text-white",
  renderPrimary,
  itemId,
  favoritable = false,
  noDrag = false,
  grip,
}: SidebarNavRowProps) => {
  const primaryClassName = "flex flex-1 min-w-0 items-center px-2 py-2 md:py-px text-left";
  const content = (
    <>
      <RowIcon className={`w-4 h-4 mr-2 shrink-0 ${iconClassName}`} />
      <span className={`text-xs 2xl:text-sm whitespace-nowrap truncate ${labelClassName}`}>
        {title}
      </span>
      {trailing}
    </>
  );
  return (
    <div
      className={`group relative flex items-center w-full rounded-md my-px hover:bg-blue-300 ${
        isSelected ? "bg-[#414587]" : "bg-blue-500"
      }`}
      data-tour={dataTour}
      data-sidebar-item-id={itemId}
      data-favoritable={itemId ? String(favoritable) : undefined}
      data-sidebar-no-drag={noDrag ? "" : undefined}
    >
      {grip}
      {renderPrimary ? (
        renderPrimary(primaryClassName, content)
      ) : to ? (
        <Link
          to={to}
          onClick={onClick}
          aria-current={isSelected ? "page" : undefined}
          className={primaryClassName}
        >
          {content}
        </Link>
      ) : (
        <button type="button" onClick={onClick} className={primaryClassName}>
          {content}
        </button>
      )}
      {accessory}
    </div>
  );
};

export { SidebarItemProject, SidebarNavRow };
